import { type ComponentType, type LazyExoticComponent, lazy } from "react"

/**
 * Reload budget, in sessionStorage so it survives the reload it is counting.
 *
 * A stale tab needs exactly one reload. Anything past that is a build that is
 * genuinely broken, and looping on it is far worse than showing an error — so
 * the budget is spent, never returned. It is deliberately not cleared on a
 * successful load: routes load concurrently, and a layout chunk succeeding
 * would otherwise hand a failing page chunk a fresh reload every time round.
 * The window instead lets a long-lived tab recover its budget for a later
 * deploy.
 *
 * Two chunks failing together therefore means the second one surfaces its error
 * instead of reloading. That is fine — the reload the first one triggered is
 * already replacing the document.
 */
const RELOAD_RECORD_KEY = "route-chunk-reloads"
const MAX_RELOADS = 1
const RELOAD_WINDOW_MS = 5 * 60 * 1000

type ReloadRecord = { at: number; count: number }

/**
 * `lazy()` for route components, hardened against a deploy landing mid-session.
 *
 * The web deploy syncs the bundle with `--delete`, so a tab still holding the
 * previous `index.html` can ask for a hashed chunk that no longer exists — the
 * import rejects and the route renders nothing. Reloading picks up the new
 * index and its current chunk names.
 */
export function lazyRoute<T extends ComponentType<never>>(
  load: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => loadRouteChunk(load))
}

/** The retry policy behind {@link lazyRoute}, separated so it can be tested. */
export async function loadRouteChunk<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load()
  } catch (error) {
    // A module that downloaded fine and then threw on evaluation is a bug in
    // the route, not a stale tab. Reloading cannot fix it and would throw away
    // whatever the user still had on screen, so let the boundary have it.
    if (!isChunkLoadFailure(error) || !consumeReloadBudget()) throw error
    window.location.reload()
    // The reload replaces the document, so never resolve: settling here would
    // briefly render a route the outgoing page is about to discard.
    return new Promise<never>(() => {})
  }
}

/**
 * Whether the import failed because its chunk could not be fetched, as opposed
 * to failing while it ran. Browsers word this differently and none of it is
 * specified, so an unrecognized message just means no reload — the error
 * surfaces exactly as it would have before.
 */
function isChunkLoadFailure(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false
  const { name, message } = error as { name?: unknown; message?: unknown }
  if (name === "ChunkLoadError") return true
  if (typeof message !== "string") return false
  return (
    // Chrome, Edge
    message.includes("Failed to fetch dynamically imported module") ||
    // Firefox
    message.includes("error loading dynamically imported module") ||
    // Safari
    message.includes("Importing a module script failed")
  )
}

/**
 * Whether a reload is still allowed, charging it to the budget if so. False
 * when the budget is spent, or when sessionStorage cannot answer — with no
 * durable record there is nothing to stop the reloading.
 */
function consumeReloadBudget(): boolean {
  const now = Date.now()
  const record = readReloadRecord()
  if (record === undefined) return false

  const spent = record && now - record.at < RELOAD_WINDOW_MS ? record.count : 0
  if (spent >= MAX_RELOADS) return false

  try {
    const next: ReloadRecord = { at: now, count: spent + 1 }
    window.sessionStorage.setItem(RELOAD_RECORD_KEY, JSON.stringify(next))
    return true
  } catch {
    return false
  }
}

/** The stored record, null if absent or unreadable, undefined if storage is. */
function readReloadRecord(): ReloadRecord | null | undefined {
  let raw: string | null
  try {
    raw = window.sessionStorage.getItem(RELOAD_RECORD_KEY)
  } catch {
    // Absent (SSR), blocked by policy, or throwing.
    return undefined
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as ReloadRecord
    // Anything unrecognizable is treated as no record rather than as a spent
    // budget: a bad value must not cost a stale tab its one real reload.
    return typeof parsed?.at === "number" && typeof parsed?.count === "number" ? parsed : null
  } catch {
    return null
  }
}
